/**
 * Typed fetch wrappers for every endpoint the candidate SPA uses.
 *
 * The contract lives in /API_CONTRACT.md. All error responses use the
 * `{ error, message }` envelope, which we surface as `ApiError`.
 */

import type {
  AnswerResponse,
  ApiErrorPayload,
  FinalizeResponse,
  InfoResponse,
  QuestionTextResponse,
  SkipResponse,
  StartResponse,
} from '../types';

const DEFAULT_BACKEND = 'http://localhost:8080';

export function getBackendUrl(): string {
  const fromEnv = import.meta.env.VITE_BACKEND_URL;
  const raw = typeof fromEnv === 'string' && fromEnv.length > 0 ? fromEnv : DEFAULT_BACKEND;
  // Strip trailing slashes so path concatenation is predictable.
  return raw.replace(/\/+$/, '');
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }

  get isExpired(): boolean {
    return this.status === 410 || this.code === 'expired';
  }

  get isNotFound(): boolean {
    return this.status === 404 || this.code === 'not_found';
  }

  get isWrongState(): boolean {
    return this.status === 409 || this.code === 'wrong_state';
  }

  get isModelsLoading(): boolean {
    return this.status === 503 || this.code === 'models_loading';
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: Partial<ApiErrorPayload> = {};
  try {
    payload = (await res.json()) as Partial<ApiErrorPayload>;
  } catch {
    // fall through — non-JSON body
  }
  const code = payload.error ?? `http_${res.status}`;
  const message = payload.message ?? `Request failed with status ${res.status}`;
  return new ApiError(code, message, res.status);
}

function base(token: string): string {
  return `${getBackendUrl()}/api/interviews/${encodeURIComponent(token)}`;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError('network_error', `Network error: ${detail}`, 0);
  }
  if (!res.ok) {
    throw await parseError(res);
  }
  // 204 No Content
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return undefined as unknown as T;
}

/**
 * GET /info — returns metadata, or an invalid marker if the token is
 * expired / unknown / already completed. The backend may return either
 * HTTP 200 with `valid: false` or HTTP 410; both are normalized here.
 */
export async function getInfo(token: string): Promise<InfoResponse> {
  const url = `${base(token)}/info`;
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET' });
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError('network_error', `Network error: ${detail}`, 0);
  }

  if (res.status === 410 || res.status === 404) {
    try {
      const body = (await res.json()) as Partial<InfoResponse>;
      if (body && body.valid === false) {
        return body as InfoResponse;
      }
    } catch {
      // ignore, fall through
    }
    const reason = res.status === 404 ? 'unknown' : 'expired';
    return { valid: false, reason };
  }

  if (!res.ok) {
    throw await parseError(res);
  }
  return (await res.json()) as InfoResponse;
}

export async function postConsent(token: string): Promise<void> {
  await request<{ ok: true }>(`${base(token)}/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ consented: true }),
  });
}

export async function postStart(token: string): Promise<StartResponse> {
  return request<StartResponse>(`${base(token)}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export async function getQuestionText(
  token: string,
  idx: number,
): Promise<QuestionTextResponse> {
  return request<QuestionTextResponse>(`${base(token)}/question/${idx}/text`, {
    method: 'GET',
  });
}

/** URL for the pre-generated question audio; fed to an <audio src>. */
export function getQuestionAudioUrl(token: string, idx: number): string {
  return `${base(token)}/question/${idx}/audio`;
}

export async function postAnswer(
  token: string,
  idx: number,
  audio: Blob,
): Promise<AnswerResponse> {
  const form = new FormData();
  // File name is cosmetic but helps when a human inspects the multipart body.
  const ext = audio.type.includes('webm')
    ? 'webm'
    : audio.type.includes('ogg')
      ? 'ogg'
      : audio.type.includes('mp4')
        ? 'm4a'
        : 'wav';
  form.append('audio', audio, `answer_${idx}.${ext}`);
  return request<AnswerResponse>(`${base(token)}/answer/${idx}`, {
    method: 'POST',
    body: form,
  });
}

export async function postSkip(token: string, idx: number): Promise<SkipResponse> {
  return request<SkipResponse>(`${base(token)}/skip/${idx}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export async function postFinalize(token: string): Promise<FinalizeResponse> {
  return request<FinalizeResponse>(`${base(token)}/finalize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export async function postAbort(token: string): Promise<void> {
  await request<{ ok: true }>(`${base(token)}/abort`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}
